import { Backdrop, Button, Card, CardContent, CircularProgress, Divider, FormControlLabel, Grid, InputAdornment, MenuItem, Select, Snackbar, Switch, TextField, Typography } from "@material-ui/core";
import { useEffect, useRef, useState } from "react";
import { Add, Remove } from "@material-ui/icons";
import { addAiCreditGrant, getAiCostSummary, getAiCreditGrants, getAiCreditLimit, getAppHealth, getFileProcessingSelection, getLocationLimit, getShowVegasMarketingSelection, repriceAllProcessingCosts, setAiCreditLimit, setFileProcessingSelection, setLocationLimit, setShowVegasMarketingSelection, voidAiCreditGrant } from "../../Actions";

// A titled card so each concern is visually grouped instead of one long scroll.
function SectionCard({ title, subtitle, children }) {
  return (
    <Card variant="outlined" style={{ height: "100%" }}>
      <CardContent>
        <Typography variant="h6" style={{ marginBottom: subtitle ? 2 : 12 }}>{title}</Typography>
        {subtitle && <Typography variant="body2" style={{ color: "#888", marginBottom: 16 }}>{subtitle}</Typography>}
        {children}
      </CardContent>
    </Card>
  );
}

// A labelled on/off switch with helper text, so a setting's purpose is obvious at a glance.
function ToggleRow({ checked, onChange, label, help }) {
  return (
    <FormControlLabel
      style={{ display: "flex", alignItems: "flex-start", margin: "6px 0" }}
      control={<Switch color="primary" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />}
      label={(
        <div style={{ paddingTop: 6 }}>
          <Typography variant="body1">{label}</Typography>
          {help && <Typography variant="caption" style={{ color: "#888" }}>{help}</Typography>}
        </div>
      )}
    />
  );
}

function statusColor(status) {
  if (status === 'ok') return '#2e7d32';
  if (status === 'degraded') return '#ef6c00';
  if (status === 'down') return '#c62828';
  return '#757575';
}

// One health check as a colored-dot row. Grey dot = informational/not-deployed.
function CheckRow({ label, check, describe }) {
  if (!check) return null;
  const notDeployed = check.status === 'not_deployed';
  const color = notDeployed ? '#9e9e9e' : check.ok === false ? '#c62828' : '#2e7d32';
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "3px 0" }}>
      <span style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: color, flexShrink: 0 }} />
      <Typography variant="body2" style={{ minWidth: 120, fontWeight: 600 }}>{label}</Typography>
      <Typography variant="body2" style={{ color: "#555" }}>
        {notDeployed ? "not deployed" : describe(check)}
      </Typography>
    </div>
  );
}

export default function Tools () {
  const [locationCount, setLocationCount] = useState('');
  const [showVegasMarketing, setShowVegasMarketing] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [fileProcessingEnabled, setFileProcessingEnabled] = useState(false);
  const [aiCost, setAiCost] = useState(null);
  const [repricingAll, setRepricingAll] = useState(false);
  const [aiCreditUsd, setAiCreditUsd] = useState('');
  const [grants, setGrants] = useState({ grants: [], totalUsd: 0, totalPoints: 0 });
  const [grantUsd, setGrantUsd] = useState('');
  const [grantNote, setGrantNote] = useState('');
  const [addingGrant, setAddingGrant] = useState(false);
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '' });

  const locationTimeoutRef = useRef(null);
  const creditTimeoutRef = useRef(null);

  const notify = (message) => setSnack({ open: true, message });

  useEffect(() => {
    async function getInitialData () {
      // Independent reads — run concurrently instead of 9 sequential round trips.
      await Promise.all([
        getLocationLimit().then((r) => setLocationCount(Math.floor(r.data))).catch(() => setLocationCount('')),
        getShowVegasMarketingSelection().then((r) => setShowVegasMarketing(Number(r.data) === 1)).catch(() => setShowVegasMarketing(false)),
        getFileProcessingSelection().then((r) => setFileProcessingEnabled(Number(r.data) === 1)).catch(() => setFileProcessingEnabled(false)),
        getAiCostSummary().then((r) => setAiCost(r.data)).catch(() => setAiCost(null)),
        getAiCreditLimit().then((r) => setAiCreditUsd(Number(r.data))).catch(() => setAiCreditUsd('')),
        getAiCreditGrants().then((r) => setGrants(r.data)).catch(() => setGrants({ grants: [], totalUsd: 0, totalPoints: 0 })),
        getAppHealth().then((r) => setHealth(r.data)).catch(() => setHealth(null)),
      ]);
      setIsPageLoading(false);
    }
    getInitialData();
  }, []);

  const refreshHealth = () => {
    setHealthLoading(true);
    getAppHealth()
      .then((r) => setHealth(r.data))
      .catch(() => setHealth(null))
      .finally(() => setHealthLoading(false));
  };

  useEffect(() => () => {
    clearTimeout(locationTimeoutRef.current);
    clearTimeout(creditTimeoutRef.current);
  }, []);

  // All saves are optimistic (update local state now) + a "Saved" toast — no full-page reload.
  const saveToggle = async (setter, apiFn, checked) => {
    setter(checked);
    await apiFn(checked ? '1' : '0').then(() => notify('Saved')).catch(() => notify('Save failed'));
  };

  const saveLocation = (value) => {
    setLocationCount(value);
    clearTimeout(locationTimeoutRef.current);
    locationTimeoutRef.current = setTimeout(async () => {
      if (value === '') return;
      await setLocationLimit(value).then(() => notify('Saved')).catch(() => notify('Save failed'));
    }, 500);
  };

  const saveCredit = (value) => {
    setAiCreditUsd(value);
    clearTimeout(creditTimeoutRef.current);
    creditTimeoutRef.current = setTimeout(async () => {
      const usd = Number(value);
      if (value === '' || !Number.isFinite(usd) || usd < 0) return;
      await setAiCreditLimit(usd).then(() => notify('Saved')).catch(() => notify('Save failed'));
    }, 600);
  };

  const handleRepriceAll = async () => {
    setRepricingAll(true);
    await repriceAllProcessingCosts().catch(() => null);
    await getAiCostSummary().then((r) => setAiCost(r.data)).catch(() => {});
    setRepricingAll(false);
    notify('Reprice complete');
  };

  const handleAddGrant = async () => {
    const usd = Number(grantUsd);
    if (!Number.isFinite(usd) || usd <= 0) return;
    setAddingGrant(true);
    const res = await addAiCreditGrant(usd, grantNote.trim() || null).catch(() => null);
    setAddingGrant(false);
    if (!res) { notify('Could not add credit'); return; }
    setGrants(res.data);
    setGrantUsd('');
    setGrantNote('');
    notify('Credit added for this month');
  };

  const handleVoidGrant = async (id) => {
    const res = await voidAiCreditGrant(id).catch(() => null);
    if (!res) { notify('Could not void credit'); return; }
    setGrants(res.data);
    notify('Credit voided');
  };

  if (isPageLoading) {
    return (
      <Backdrop open={true}>
        <CircularProgress size={100} style={{ color: "#FFFFFF" }} />
      </Backdrop>
    );
  }

  const TOOLS = [
    { label: 'AI Prompt', href: '/ai/prompt' },
    { label: 'Label Scanner', href: '/ai/label-scan' },
    { label: 'Flow View', href: '/calculations/flow' },
    { label: 'Help Video Review', href: '/help-video-review' },
  ];

  return (
    <div style={{ padding: "30px 20px", maxWidth: 1100, margin: "0 auto" }}>
      <Typography variant="h4" style={{ marginBottom: 20 }}>Internal Tools</Typography>

      <Grid container spacing={3} alignItems="stretch">
        <Grid item xs={12}>
          <SectionCard title="Application Health" subtitle="Live status of this site — database, migrations, search index, and duplicate detection.">
            {health ? (
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                  <span style={{ padding: "2px 12px", borderRadius: 12, color: "#fff", fontWeight: 700, fontSize: 13, backgroundColor: statusColor(health.status) }}>
                    {String(health.status || "unknown").toUpperCase()}
                  </span>
                  <Typography variant="body2" style={{ color: "#888" }}>
                    v{health.version} · {health.db}
                  </Typography>
                  <Button size="small" onClick={refreshHealth} disabled={healthLoading} style={{ marginLeft: "auto" }}>
                    {healthLoading ? <CircularProgress size={16} /> : "Refresh"}
                  </Button>
                </div>
                <CheckRow label="Database" check={health.checks?.database} describe={(c) => (c.ok ? `OK · ${c.latency_ms}ms` : `Error: ${c.error}`)} />
                <CheckRow label="Migrations" check={health.checks?.migrations} describe={(c) => (c.ok ? `Clean · ${c.attempted} script(s)` : `Failed: ${(c.failed || []).join(", ") || "unknown"}`)} />
                <CheckRow label="Search index" check={health.checks?.search_index} describe={(c) => `backlog ${c.dirty_backlog}${c.refresh_enabled ? "" : " · refresh OFF"}`} />
                <CheckRow label="Duplicate scan" check={health.checks?.duplicate_scan} describe={(c) => `${c.enabled ? "on" : "off"}${c.printed_only ? " · printed-only" : ""} · last ${c.last_status || "—"}`} />
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Typography variant="body2" style={{ color: "#888" }}>Health unavailable.</Typography>
                <Button size="small" onClick={refreshHealth} disabled={healthLoading}>
                  {healthLoading ? <CircularProgress size={16} /> : "Retry"}
                </Button>
              </div>
            )}
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard title="Tools">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              {TOOLS.map((t) => (
                <Button key={t.href} variant="contained" href={t.href}>{t.label}</Button>
              ))}
            </div>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard title="Site Settings">
            <Typography variant="body2" style={{ fontWeight: "bold", marginBottom: 6 }}>Location limit</Typography>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20 }}>
              <Button size="small" variant="outlined" onClick={() => saveLocation(Math.max(0, (Number(locationCount) || 0) - 1))}><Remove fontSize="small" /></Button>
              <TextField
                size="small"
                type="number"
                variant="outlined"
                style={{ width: 100 }}
                value={locationCount}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === '') { setLocationCount(''); return; }
                  const next = Math.max(0, Math.floor(Number(raw)));
                  if (Number.isNaN(next)) return;
                  saveLocation(next);
                }}
              />
              <Button size="small" variant="outlined" onClick={() => saveLocation((Number(locationCount) || 0) + 1)}><Add fontSize="small" /></Button>
            </div>

          </SectionCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard title="Pricing" subtitle="Graphs are created and edited in the Flow View. Which model prices the site is selected on the Cost Adjustments page.">
            <Button size="small" variant="outlined" href="/calculations/flow">
              Edit Custom Pricing
            </Button>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard title="Feature Toggles">
            <ToggleRow
              checked={showVegasMarketing}
              onChange={(c) => saveToggle(setShowVegasMarketing, setShowVegasMarketingSelection, c)}
              label="Show Vegas marketing"
              help="Surfaces the Vegas market lead capture on this site."
            />
            <ToggleRow
              checked={fileProcessingEnabled}
              onChange={(c) => saveToggle(setFileProcessingEnabled, setFileProcessingSelection, c)}
              label="Enable File Processing page"
              help="Lets this site's admins use the AI / rule file-processing page. Hidden users always have access."
            />
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard title="AI Credits" subtitle="Customers see points; 1 credit = $0.0001, so $1 = 10,000 pts.">
            <Typography variant="body2" style={{ fontWeight: "bold", marginBottom: 6 }}>Monthly allotment</Typography>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
              <TextField
                size="small"
                type="number"
                variant="outlined"
                style={{ width: 130 }}
                value={aiCreditUsd}
                onChange={(e) => saveCredit(e.target.value === '' ? '' : Number(e.target.value))}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />
              {aiCreditUsd !== '' && Number.isFinite(Number(aiCreditUsd)) && (
                <Typography variant="body2" style={{ color: "#888" }}>
                  = {(Number(aiCreditUsd) * 10000).toLocaleString()} pts / month
                </Typography>
              )}
            </div>

            <Divider style={{ margin: "4px 0 12px" }} />

            <Typography variant="body2" style={{ fontWeight: "bold" }}>One-month credit</Typography>
            <Typography variant="caption" style={{ color: "#888", display: "block", marginBottom: 8 }}>
              Adds to this month only, then reverts to the base. {grants.totalUsd > 0 && (
                <span style={{ color: "#2e7d32", fontWeight: 600 }}>
                  This month: +${Number(grants.totalUsd).toFixed(2)} ({Number(grants.totalPoints).toLocaleString()} pts)
                </span>
              )}
            </Typography>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
              <TextField
                size="small"
                type="number"
                variant="outlined"
                placeholder="Amount"
                style={{ width: 110 }}
                value={grantUsd}
                onChange={(e) => setGrantUsd(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />
              <TextField
                size="small"
                variant="outlined"
                placeholder="Note (optional)"
                style={{ flex: 1, minWidth: 120 }}
                value={grantNote}
                onChange={(e) => setGrantNote(e.target.value)}
              />
              <Button
                size="small"
                variant="contained"
                disabled={addingGrant || !(Number(grantUsd) > 0)}
                style={{ backgroundColor: (addingGrant || !(Number(grantUsd) > 0)) ? undefined : "#2e7d32", color: "#FFFFFF" }}
                onClick={handleAddGrant}
              >
                {addingGrant ? <CircularProgress size={18} style={{ color: "#FFFFFF" }} /> : "Add"}
              </Button>
            </div>
            {grants.grants && grants.grants.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                {grants.grants.map((g) => (
                  <div key={g.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "2px 0" }}>
                    <Typography variant="caption" style={{ color: "rgba(0,0,0,0.7)" }}>
                      +${Number(g.amount_usd).toFixed(2)} <span style={{ color: "#888" }}>({(Number(g.amount_usd) * 10000).toLocaleString()} pts)</span>
                      {g.note ? ` — ${g.note}` : ''}
                    </Typography>
                    <Button size="small" style={{ color: "#f44336", minWidth: 0 }} onClick={() => handleVoidGrant(g.id)}>Void</Button>
                  </div>
                ))}
              </div>
            )}

            <Divider style={{ margin: "4px 0 12px" }} />

            {aiCost ? (
              <div>
                <Typography variant="body2">
                  <b>This month:</b> ${(Number(aiCost.monthUsd) || 0).toFixed(2)}
                  <span style={{ color: "#888" }}> ({aiCost.monthRunCount} run{aiCost.monthRunCount === 1 ? '' : 's'})</span>
                </Typography>
                <Typography variant="body2">
                  <b>All time:</b> ${(Number(aiCost.totalUsd) || 0).toFixed(2)}
                  <span style={{ color: "#888" }}> ({aiCost.runCount} run{aiCost.runCount === 1 ? '' : 's'})</span>
                </Typography>
                {(aiCost.unavailableCount > 0 || aiCost.awaitingSplitCount > 0) && (
                  <Typography variant="caption" style={{ color: "#b26a00", display: "block", marginTop: 6 }}>
                    {aiCost.unavailableCount > 0 && `${aiCost.unavailableCount} run(s) captured but not priced (re-priceable). `}
                    {aiCost.awaitingSplitCount > 0 && `${aiCost.awaitingSplitCount} run(s) have no token split and can't be priced.`}
                  </Typography>
                )}
                {aiCost.unavailableCount > 0 && (
                  <Button
                    size="small"
                    variant="contained"
                    disabled={repricingAll}
                    style={{ marginTop: 12, backgroundColor: "#142e3e", color: "#28c4fc" }}
                    onClick={handleRepriceAll}
                  >
                    {repricingAll ? <CircularProgress size={18} style={{ color: "#28c4fc" }} /> : `Reprice ${aiCost.unavailableCount} unpriced`}
                  </Button>
                )}
              </div>
            ) : (
              <Typography variant="body2" style={{ color: "#888" }}>No AI cost data yet.</Typography>
            )}
          </SectionCard>
        </Grid>
      </Grid>

      <Snackbar
        open={snack.open}
        autoHideDuration={2000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        message={snack.message}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </div>
  );
}
